import { Injectable } from '@angular/core';
import { BaseComponent } from '@wms/app/components';
import { BaseVariableService, VARIABLE_CONSTANTS } from '@wms/variables';
import { extractType, isEmptyObject, isString, WS_CONSTANTS } from '@wms/core';
import { ServiceFactoryService, TypeFactoryService } from '@wms/app/services';
import { includes, without, reject, filter, isEmpty, find, findKey, forEach, get, each, every, isNull, sortBy, compact } from 'lodash-es'
import { isObject } from '@wm/core';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class WmsCrudVariableService extends BaseComponent {
  SERVICE_TYPE_REST = VARIABLE_CONSTANTS.SERVICE_TYPE_REST;
  SV_OP_WRAPPER = "_sv_operation_wrapper";
  CONTROLLER_OPERATION_SEPARATOR = "~wm_separator~";
  serviceOperations: any = {};
  sampleResponse: any;
  CONTROLLER_TYPE_QUERY = 'QueryExecution';
  sampleParamsValues: any;
  bindMapCollection: any;
  HANDLE_SOAP_PARAMS: boolean = false;
  dataTypes: any;
  propertyGroupChangeSubject = new Subject();
  showTabs: boolean = false;
  currentTab: any;

  constructor(private baseVariableService: BaseVariableService, private serviceFactory: ServiceFactoryService, private typeFactory: TypeFactoryService) {
    super();
    this.dataTypes = this.typeFactory.getTypes();
  }

  __getVariableDependencies() {
    let variable = this.baseVariableService.selectedVariable.variableObject,
      serviceType = variable.serviceType,
      rejectionList = [VARIABLE_CONSTANTS.SERVICE_TYPE_WEBSOCKET];

    if (serviceType) {
      this.baseVariableService.selectedVariable.propertyObj.service.editonly = false;
      this.baseVariableService.selectedVariable.propertyObj.operation.editonly = false;
      if (includes(VARIABLE_CONSTANTS.REST_SUPPORTED_SERVICES, serviceType)) {
        rejectionList = without(VARIABLE_CONSTANTS.REST_SUPPORTED_SERVICES, serviceType);
        rejectionList.push(VARIABLE_CONSTANTS.SERVICE_TYPE_MODEL);
      }
    } else {
      this.baseVariableService.selectedVariable.propertyObj.service.editonly = true;
    }

    this.baseVariableService.selectedVariable.propertyObj.operation.editonly = false;

    /*get the list of services available and append the options for service property*/
    (this.serviceFactory.getServicesWithType() as Promise<any>).then((services: any) => {
      services = reject(services, (service: any) => includes(rejectionList, service.type));
      services.sort((prevItem: any, currentItem: any) => prevItem.name.localeCompare(currentItem.name));

      /*push the services in the options list for the respective property selectbox*/
     
      if ( this.baseVariableService.selectedVariable.propertyObj.service ) {
        this.baseVariableService.selectedVariable.propertyObj.service.options = {};
        services.forEach((serviceObj: any) => {
          this.baseVariableService.selectedVariable.propertyObj.service.options[serviceObj.name] = serviceObj.name;
        });
      }


      /*append the options for operations property based on selected service property*/
      this.__onPropertyChange('service', undefined, true);
    });

    /* apply check for transformationRequired field */
    this.__onPropertyChange('transformationRequired', undefined, true);
  }

  __setServiceOperations(operations: any, selectedVariable: any, initFlag: any, serviceType: any) {
    let operation,
      opController,
      opKey,
      oldOperationObj: any;
    selectedVariable[this.SV_OP_WRAPPER] = undefined;
    oldOperationObj = find(operations, { 'operationId': selectedVariable.variableObject.operationId });
    operations.forEach((operation: any) => {
      //exclude all deprecated operations except current variable's operation
      if (operation.deprecated && operation.operationId !== (oldOperationObj && oldOperationObj.operationId)) {
        return;
      }
      opController = operation.controller ? operation.controller.replace(/Controller$/, '') : '';
      opKey = opController + this.CONTROLLER_OPERATION_SEPARATOR + operation.name;
      selectedVariable.propertyObj.operation.options[opKey] = {
        type: opController,
        name: operation.name
      };
      this.serviceOperations[opKey] = operation;
    });
    /* operation change the modelObj */
    selectedVariable.propertyObj.operation.modelObj = selectedVariable;
    selectedVariable.propertyObj.operation.modelKey = this.SV_OP_WRAPPER;
    if (initFlag && selectedVariable.variableObject['operation'] && selectedVariable.variableObject['operation'] !== '') {
      if (selectedVariable.variableObject.controller) {
        selectedVariable[this.SV_OP_WRAPPER] = selectedVariable.variableObject['controller'] + this.CONTROLLER_OPERATION_SEPARATOR + selectedVariable.variableObject['operation'];
      } else {
        /* backward compatibility, controller not found, find the first one with active variable's operation */
        let indexKey: any = findKey(this.serviceOperations, (op: any) => {
          return op.name === selectedVariable.variableObject['operation'];
        });
        operation = this.serviceOperations[indexKey];
        opController = operation ? operation.controller : '';
        opController = opController ? opController.replace(/Controller$/, '') : '';
        selectedVariable[this.SV_OP_WRAPPER] = opController + this.CONTROLLER_OPERATION_SEPARATOR + selectedVariable.variableObject['operation'];
      }

      this.__onPropertyChange('operation', undefined, true);
    }
  }

  __updateCrudService(key: any, currentTabSelection?: boolean) {
    if (currentTabSelection) {
      this.baseVariableService.skipSelectingCurrentTab = true;
    } else {
      this.baseVariableService.skipSelectingCurrentTab = false;
    }
    
    this.currentTab = key;
    /*temp fix to rerender the tabs on variable change because ng-repeat markup is not getting refreshed otherwise.*/
    this.showTabs = true;
    //required to show proper fields in property binding dialog
    this.baseVariableService.selectedVariable.currentTab = key;
  };

  /*function to handle property change for a variable*/
  __onPropertyChange(propertyName: any, value?: any, initFlag?: any) {
    let selectedService,
      selectedOperation: any = '',
      selectedOperationObj,
      serviceType: any,
      selectedVariable = this.baseVariableService.selectedVariable,
      hideDataServiceOptions = true,
      hideTransformationOptions = false,
      returnType,
      isList,
      transformationRequired,
      columnField,
      processColumns,
      targetOperationType,
      isServiceWithOneOperation;

    if (!initFlag) {
      this.baseVariableService.markUpdated();
    }

    selectedService = selectedVariable.variableObject['service'];
    /* check for service type */
    serviceType = (this.serviceFactory.getServiceObjectByName(selectedService) as any).type;

    /*If 'service' property changed, change the operation property as well*/
    if (propertyName === 'service') {
      if (!initFlag) {
        selectedVariable.propertyObj.operation.modelObj[selectedVariable.propertyObj.operation.modelKey] = '';
      }
      if (selectedVariable.propertyObj.operation) {
        selectedVariable.propertyObj.operation.options = [];
      }
      selectedService = selectedVariable.variableObject['service'];

      /* if service not selected for the variable */
      if (!selectedService) {
        this.baseVariableService.emptyDataBindingObject(selectedVariable);
        return;
      }

      /* append the service type to the variable */
      selectedVariable.variableObject['serviceType'] = serviceType || "";

      /* decide on displaying data-transformation related properties */
      hideTransformationOptions = serviceType !== this.SERVICE_TYPE_REST;
      selectedVariable.propertyObj['transformationRequired'].hide = hideTransformationOptions;
      selectedVariable.variableObject['transformationRequired'] = hideTransformationOptions ? false : selectedVariable.variableObject['transformationRequired'];
      this.__onPropertyChange('transformationRequired', undefined, true);
      /*get the list of operations for the selected service*/

      this.baseVariableService.variableDialogMsg = {
        'type': 'loading',
        'caption': this.locale.MESSAGE_SERVICE_INFO_LOADING
      };
      (this.serviceFactory.getServiceOperations(selectedService) as any).then((operations: any) => {
        this.baseVariableService.variableDialogMsg = null;
        selectedVariable.propertyObj.operation.options = {};
        targetOperationType = selectedVariable.variableObject._operationType;
        if (targetOperationType) {
          let targetController = targetOperationType === 'query' ? 'QueryExecution' : (targetOperationType === 'procedure' ? 'ProcedureExecution' : '');
          const tmpOperations = filter(operations, { controller: targetController });
          operations = tmpOperations.length ? tmpOperations : operations;
        }
        operations.sort((prevItem: any, currentItem: any) => prevItem.name.localeCompare(currentItem.name))

        isServiceWithOneOperation = includes(VARIABLE_CONSTANTS.WEB_SERVICE_VARIABLE_WITH_NO_OPERATION, serviceType);
        selectedVariable.propertyObj.operation.hide = true;
        if (operations.length === 1 && !initFlag) {
          selectedVariable.variableObject.operation = operations[0].name;
          selectedVariable.variableObject.controller = operations[0].controller;
          initFlag = true;
        }
        this.__setServiceOperations(operations, selectedVariable, initFlag, serviceType);
        if (initFlag === true) {
          this.baseVariableService.sourceExists();
        }
      }).catch(() => {
        this.baseVariableService.sourceExists();
        this.baseVariableService.variableDialogMsg = null;
      });
    }

    /*If the 'operation' property changed, change the data properties*/
    if (propertyName === 'operation' || propertyName === this.SV_OP_WRAPPER) {
      this.baseVariableService.isFileUploadTypeVar(selectedVariable);
      selectedService = selectedVariable.variableObject['service'];
      selectedOperation = selectedVariable[this.SV_OP_WRAPPER];
      selectedOperationObj = filter(this.serviceOperations, (op: any) => {
        return op.crudOperationId === selectedVariable.variableObject.crudOperationId
      })[0] || {};
      this.sampleResponse = undefined;

      /* re-format operation and persist it */
      selectedOperation = selectedOperation && selectedOperation.split(this.CONTROLLER_OPERATION_SEPARATOR).pop();
      selectedVariable.variableObject.operation = selectedOperation;
      selectedVariable.variableObject.controller = get(selectedOperationObj, 'controller') ? selectedOperationObj.controller : '';

      /* decide on displaying query related properties */
      hideDataServiceOptions = selectedVariable.variableObject.controller !== this.CONTROLLER_TYPE_QUERY;
      selectedVariable.propertyObj['maxResults'].hide = hideDataServiceOptions;
      selectedVariable.propertyObj['orderBy'].hide = hideDataServiceOptions;
      //set deprecated for variables which had deprecated operation
      selectedVariable.variableObject._deprecated = selectedOperationObj.deprecated;
      /*check value sanity*/
      if (!selectedService || !selectedOperation || selectedOperation === '') {
        this.baseVariableService.emptyDataBindingObject(selectedVariable);
        return;
      }

      /*get the list of operations for the selected service*/

      this.baseVariableService.variableDialogMsg = {
        'type': 'loading',
        'caption': this.locale.MESSAGE_SERVICE_INFO_LOADING
      };
      (this.serviceFactory.getCrudServiceOperationParams(selectedService, selectedOperationObj.crudOperationId, 'all') as Promise<any>).then((operations: any) => {
        this.baseVariableService.variableDialogMsg = null;
        let dataSetSubGroup: any,
          modelNodesArray,
          errorMsg,
          modelNodesObj: any = {},
          oldBindingMap = {};
        if (!initFlag) {
          /*initialize the data binding object for the variable*/
          selectedVariable.variableObject.dataBinding = [];
        } else if (typeof selectedVariable.variableObject.dataBinding === 'object' && !Array.isArray(selectedVariable.variableObject.dataBinding)) {
          /*
           * In old implementation, dataBinding is an object map like:
           * dataBinding: {"field1": "value1", "field2": "value2}
           * If such object is found, extract it and persist for migration
           */
          oldBindingMap = selectedVariable.variableObject.dataBinding;
          //selectedVariable.variableObject.dataBinding = [];
        }

        this.baseVariableService.emptyDataBindingObject(selectedVariable);

        /* find the dataSet subgroup out of properties */
        this.baseVariableService.propertyGroups.forEach((parentGroup: any) => {
          parentGroup.subGroups.forEach((subGroup: any) => {
            if (subGroup.name === "inputfields") {
              dataSetSubGroup = subGroup;
            }
          });
        });

        /* remove previous properties from the dataSet subgroup */
        dataSetSubGroup.properties = [];

        if (serviceType === this.SERVICE_TYPE_REST && selectedVariable.isNew) {
          this.sampleParamsValues = {};
          this.bindMapCollection = [];
          // assignRestParameterDetails(selectedVariable.variableObject.service); - TO DO - Needed?
        }


        /*special case for params of a SOAP service*/
        if (this.HANDLE_SOAP_PARAMS) {
          /* loop over each operation parameter */
          operations.parameter.forEach((parameter: any) => {
            if (parameter.typeRef && this.dataTypes[parameter.typeRef] && this.dataTypes[parameter.typeRef].fields && !isEmptyObject(this.dataTypes[parameter.typeRef].fields)) {
              /* if parameter object has fields inside, push them*/
              Object.keys(this.dataTypes[parameter.typeRef].fields).forEach((subParamName: any) => {
                let subParam: any = this.dataTypes[parameter.typeRef].fields[subParamName];
                this.baseVariableService.addParamToProperties({ name: subParamName, typeRef: subParam.type, isList: subParam.isList }, selectedVariable, dataSetSubGroup, this.dataTypes, parameter.name || (parameter.parameterType && parameter.parameterType.toLowerCase()));
              });
            } else {
              /* else, simply push the parameter */
              if (parameter.parameterType !== 'OUT') {
                this.baseVariableService.addParamToProperties(parameter, selectedVariable, dataSetSubGroup, this.dataTypes);
              }
            }
          });
        } else {
          /* case for service other than SOAP */
          let currentOps: any = [];
          operations.forEach((operation: any) => {
            currentOps.push(operation.operationType);
            operation.parameter.forEach((parameter: any) => {
              if (parameter.parameterType !== 'OUT') {
                this.__prepareBindingObjectList(parameter, selectedVariable, oldBindingMap, operation);
              }
            });
          });
          let opsInfo = this.baseVariableService.tabOrder.filter((item: any) => currentOps.includes(item));
          this.__updateCrudService(opsInfo.length && opsInfo[0] || 'create', true);
        }

        if (selectedVariable.variableObject._deprecated) {
          forEach(selectedVariable.variableObject.dataBinding, (binding: any) => {
            binding.invalid = !find(operations.parameter, { "name": binding.target }) || undefined;
          });
          errorMsg = isEmpty(operations.parameter) ? this.locale.MESSAGE_DEPRECATED_OPERATION : this.locale.MESSAGE_INVALID_PARAMETER;
          // TO DO
          // errorMsg = errorMsg + '<a target="_blank" style=\"cursor:pointer\" href="' + EXTERNAL_LINK_CONSTANTS.DATABASE_SERVICES + '">More info</a>';
          // $scope.toggleMessage(true, errorMsg, "warn");
        } else {
          // Do not hide a loading message for an async request.
          // if (!$scope.disableDialogActions) { -- TO DO
          //   $scope.toggleMessage(false);
          // }

          // Update data types for inner referenced nodes.
          each(operations, (operation: any) => {
            if (!(includes(WS_CONSTANTS.NON_BODY_HTTP_METHODS, operation.operationType))) {
              each(selectedVariable.variableObject.dataBinding, (dbinding) => {
                dbinding.forEach((binding: any) => {
                  if (!binding.type && operation.parameter.length) {
                    binding.type = this.typeFactory.getChildNodeType(operation.parameter[0].typeRef, binding.target.split('.').splice(1).join('.'))
                  }
                });
              });

            }
          });

        }

        /* add prepared bindings to the property object for display */
        this.baseVariableService.addBindingsToProperties();

        /* set the type of the variable to the return type of the operation */
        let listOperation = find(operations, {'operationType': 'list'}) || operations[0];
        operations.return = listOperation?.return || {};
        returnType = listOperation?.return.typeRef;
        isList = listOperation?.isList || false;
        selectedVariable.variableObject.type = returnType;
        selectedVariable.variableObject.isList = isList;

        /* get the data model based on the return type of service-operation */
        modelNodesArray = this.typeFactory.getFieldsForType(selectedVariable.variableObject.type, { 'filter': 'all' }).sort();

        /* default none value */
        modelNodesObj['--none--'] = "--none--";
        modelNodesArray.forEach((model: any) => {
          modelNodesObj[model] = model;
        });

        selectedVariable.propertyObj.columnField.options = modelNodesObj;
        selectedVariable.propertyObj.dataField.options = modelNodesObj;

        if (VARIABLE_CONSTANTS.REST_SUPPORTED_SERVICES.indexOf(serviceType) !== -1) {
          //Marking variable dirty if operationId has got updated
          if (selectedVariable.variableObject.operationId !== operations.operationId) {
            selectedVariable.variableObject.operationId = selectedVariable.variableObject.operationId || operations.operationId;
            this.baseVariableService.markUpdated();
          }
        }
      }).catch(() => {
        this.baseVariableService.sourceExists();
        this.baseVariableService.variableDialogMsg = null;
      });
    }

    if (propertyName === 'transformationRequired') {
      transformationRequired = selectedVariable.variableObject['transformationRequired'];
      selectedVariable.propertyObj.columnField.hide = !transformationRequired;
      selectedVariable.propertyObj.dataField.hide = !transformationRequired;

      if (!initFlag && !this.sampleResponse) {
        this.baseVariableService.getRestServiceDetails(selectedVariable.variableObject.service, (response: any) => {
          this.sampleResponse = this.baseVariableService.extractRESTSampleResponseData(response);
        });
      }
    }

    if (propertyName === 'columnField') {
      columnField = selectedVariable.variableObject.columnField;
      selectedVariable.variableObject['transformationColumns'] = [];

      /* if column field invalid, remove transformationColumns field from variable */
      if (!columnField || columnField === '--none--') {
        delete selectedVariable.variableObject['transformationColumns'];
        return;
      }

      processColumns = (columns: any) => {
        /* sanity check */
        if (!Array.isArray(columns) || !isString(columns[0])) {
          return;
        }
        columns = columns.map(function (e) {
          return isString(e) && e.replace(/[^a-zA-Z0-9_]/g, '_');
        });
        selectedVariable.variableObject['transformationColumns'] = columns;
      };

      if (isObject(this.sampleResponse)) {
        processColumns(get(this.sampleResponse, columnField));
      } else {
        // TO DO
        // $scope.showToaster({ type: 'error', title: 'Response Body Error', description: 'This option is applicable only if the target service returns JSON or XML type response body' });
        return;
      }
    }

    /*executing the event change method to create callback variables.*/
    if (VARIABLE_CONSTANTS.EVENTS.indexOf(propertyName) > -1) {
      this.baseVariableService.onEventChange(propertyName, value);
    }
    const isEmptyVal = every(value, v => v === '' || isNull(v))

    if (propertyName === 'spinnerContext' && isEmptyVal) {
      return;
    }
    this.propertyGroupChangeSubject.next(true);
  };

  /* function to add a parameter to the selected variable properties & make it visible in the UI */
  __prepareBindingObjectList(param: any, variable: any, oldBindings: any, operation: any) {
    let paramReference: any,
      paramsCollection = [],
      operationTypeMap = { 'list': 'list', 'create': 'create', 'update': 'update', 'delete': 'delete' },
      paramName = param.name || (param.parameterType && param.parameterType.toLowerCase());
    paramName = includes(paramName, '.') ? this.baseVariableService.wrapTargetKey(paramName) : paramName;

    each(variable.variableObject.dataBinding[operation.operationType], (innerProp: any, innerPropKey: any) => {
      if (innerProp.target === paramName) {
        paramReference = innerProp;
      }
    });

    /* reference not found, add to the fields collection and process internal nodes based on type meta info for param */
    if (!paramReference) {
      if (operation.operationType === 'list') {
        variable.variableObject.dataBinding.list = variable.variableObject.dataBinding.list || [];
        paramsCollection = variable.variableObject.dataBinding.list;
      } else if (operation.operationType === 'create') {
        variable.variableObject.dataBinding.create = variable.variableObject.dataBinding.create || [];
        paramsCollection = variable.variableObject.dataBinding.create;
      } else if (operation.operationType === 'update') {
        variable.variableObject.dataBinding.update = variable.variableObject.dataBinding.update || [];
        paramsCollection = variable.variableObject.dataBinding.update;
      } else if (operation.operationType === 'delete') {
        variable.variableObject.dataBinding.delete = variable.variableObject.dataBinding.delete || [];
        paramsCollection = variable.variableObject.dataBinding.delete;
      }
      paramReference = paramsCollection[paramsCollection.push({
        "target": paramName,
        "type": param.typeRef,
        "isList": param.isList,
        "format": param.format
      }) - 1];
      if (oldBindings[param.name] && typeof oldBindings[param.name] !== 'object') {
        paramReference.value = oldBindings[param.name];
      }

    } else if (paramReference.type !== param.typeRef) {
      /* update the type ref of param in existing reference */
      paramReference.type = param.typeRef;
      paramReference.isList = param.isList;
    }
    paramReference.format = param.format;

    // for params with name not found, add subscript as data type
    if (!param.name) {
      paramReference.subscript = extractType(param.typeRef);
    }
  }
}
